import { Outlet, NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Handshake,
  Building2,
  Briefcase,
  MessageSquare,
  ScrollText,
  BarChart3,
  ChevronLeft,
  Menu,
  ShieldCheck,
  Users,
  ListFilter,
  Settings,
  LogOut,
  GraduationCap,
  Bot,
  CheckCircle2,
  Database,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useMemo } from "react";
import { useLogout } from "@/hooks/useLogout";
import { useUserRole } from "@/hooks/useUserRole";
import logoImage from "@/assets/logo-corre-digital.png";

const allNavItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/todas-vagas", icon: Database, label: "Todas as Vagas" },
  { to: "/admin/robo-vagas", icon: Bot, label: "Robô de Vagas" },
  { to: "/admin/vagas", icon: Briefcase, label: "Revisão de Vagas" },
  { to: "/admin/vagas-ativas", icon: CheckCircle2, label: "Vagas Ativas" },
  { to: "/admin/publicar-vagas", icon: Briefcase, label: "Minhas Vagas" },
  { to: "/admin/vitrine", icon: Users, label: "Vitrine Talentos" },
  { to: "/admin/shortlists", icon: ListFilter, label: "Shortlists" },
  { to: "/admin/pedidos-parceria", icon: Handshake, label: "Pedidos Parceria" },
  { to: "/admin/parceiros", icon: Building2, label: "Parceiros" },
  { to: "/admin/pedidos-contato", icon: MessageSquare, label: "Pedidos Contato" },
  { to: "/admin/emails", icon: Mail, label: "Emails" },
  { to: "/admin/matriculas", icon: GraduationCap, label: "Matrículas" },
  { to: "/admin/relatorios", icon: BarChart3, label: "Relatórios" },
  { to: "/admin/auditoria", icon: ScrollText, label: "Auditoria" },
  { to: "/admin/configuracoes", icon: Settings, label: "Configurações" },
];

const revisorPaths = new Set([
  "/admin/robo-vagas",
  "/admin/vagas",
  "/admin/vagas-ativas",
]);

export function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const handleLogout = useLogout("admin");
  const { isAdmin } = useUserRole();

  const navItems = useMemo(
    () => isAdmin ? allNavItems : allNavItems.filter((item) => revisorPaths.has(item.to)),
    [isAdmin]
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside
        className={cn(
          "fixed left-0 top-0 h-full bg-card border-r border-border transition-all duration-300 z-40",
          collapsed ? "w-16" : "w-64"
        )}
      >
        <div className="flex flex-col h-full">
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            {!collapsed && (
              <div className="flex items-center gap-2">
                <img src={logoImage} alt="Corre Digital" className="h-7 object-contain" />
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                  <ShieldCheck className="w-3 h-3" /> {isAdmin ? "ADMIN" : "REVISOR"}
                </span>
              </div>
            )}
            <Button variant="ghost" size="icon-sm" onClick={() => setCollapsed(!collapsed)} className="ml-auto">
              {collapsed ? <Menu className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
          </div>

          <nav className="flex-1 py-4 px-2 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors duration-200",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                      )
                    }
                  >
                    <item.icon className="w-5 h-5 shrink-0" />
                    {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-2 border-t border-border">
            <button
              onClick={handleLogout}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl w-full text-destructive hover:bg-destructive/10 transition-colors",
              )}
            >
              <LogOut className="w-5 h-5 shrink-0" />
              {!collapsed && <span className="text-sm font-medium">Sair</span>}
            </button>
          </div>

          {!collapsed && (
            <div className="p-4 border-t border-border">
              <div className="text-xs text-muted-foreground">
                <p className="font-medium text-foreground">Corre Digital Admin</p>
                <p>Painel de governança</p>
              </div>
            </div>
          )}
        </div>
      </aside>

      <main className={cn("flex-1 transition-all duration-300", collapsed ? "ml-16" : "ml-64")}>
        <Outlet />
      </main>
    </div>
  );
}
